//@flow
import { matchPath } from 'react-router-dom';
import queryString from 'query-string';

import {
    v2ScreensUrlEnums,
    v1ScreensUrlEnums,
    v1ScreenNames,
    v2ScreenNames
} from 'common/enums/LiveViewEnums';
import { DashboardURLEnum } from 'common/enums/DashboardURLEnum';
import type { Location } from 'react-router-dom';

const WHITE_LABELING_CLIENTID_SEPARATOR = '_';

export const getUrlNames = () => {
    const CalendarUrl = {
        path: '/client/:clientId/batch',
        DashboardURLEnum: DashboardURLEnum.Configure
    };
    const BatchUrl = {
        path: `${CalendarUrl.path}/:batchId`,
        DashboardURLEnum: DashboardURLEnum.Batch
    };
    const EditPlanUrl = {
        path: `${BatchUrl.path}/plan/:planId`,
        DashboardURLEnum: DashboardURLEnum.EditPlan
    };
    const ViewPlanUrl = {
        path: `${EditPlanUrl.path}/view`,
        DashboardURLEnum: DashboardURLEnum.ViewPlan
    };
    const UploadReviewUrl = {
        path: `${BatchUrl.path}/upload/review`,
        DashboardURLEnum: DashboardURLEnum.Review
    };
    const UploadGeocodeUrl = {
        path: `${BatchUrl.path}/upload/geocode`,
        DashboardURLEnum: DashboardURLEnum.Geocode
    };
    const ConfigureUrl = {
        path: `${BatchUrl.path}/configure`,
        DashboardURLEnum: DashboardURLEnum.Configure
    };
    return [
        CalendarUrl,
        BatchUrl,
        EditPlanUrl,
        UploadReviewUrl,
        UploadGeocodeUrl,
        ConfigureUrl,
        ViewPlanUrl
    ];
};

export const getCustomLocationMatch = (
    location: Location
): ?{
    match: $FixMe,
    url: {
        path: string,
        DashboardURLEnum: $Values<typeof DashboardURLEnum>
    }
} => {
    if (!location) {
        throw new Error('Location object missing');
    }
    const urls = getUrlNames();
    let matchedPath = null;
    urls.forEach(url => {
        const currentMatch = matchPath(location.pathname, { path: url.path });
        if (currentMatch) {
            matchedPath = {
                match: currentMatch,
                url
            };
        }
    });
    return matchedPath;
};

export const parseUrlClientId = function (location: Location): ?string {
    /**
     * finds the clientId by
     * looking for the value after `client/` and before `/`
     * */
    return matchPath(location.pathname, {
        path: `/client/:clientId`
    })?.params.clientId;
};

export const getQueryParams = (location: Location): any => {
    return location.query || queryString.parse(location.search);
};

export const getPageNameFromUrl = (match: Match, query: any): string => {
    // Not dependent on match, can be triggered from any v1 or v2 screen
    if (query.selectedTaskId) {
        return v2ScreenNames.TaskDetails;
    }

    switch (match.path) {
        case v2ScreensUrlEnums.Tasks:
            return v2ScreenNames.Tasks;

        case v2ScreensUrlEnums.Tours:
            return `${v2ScreenNames.Tours} - ${query.view}`;

        case v2ScreensUrlEnums.SingleTour:
            return `${v2ScreenNames.SingleTour} - ${query.view}`;

        case v1ScreensUrlEnums.LiveView: {
            if (query.view === 'user_report') {
                return v1ScreenNames.RiderTable;
            }
            return v1ScreenNames.TaskTable;
        }

        case v1ScreensUrlEnums.SingleRider: {
            return v1ScreenNames.SingleRider;
        }

        default:
            return match.path;
    }
};

export const extractClientAndProviderInfo = (
    clientId: string
): { clientId: string, providerId: ?string } => {
    const isWhiteLabeledClient = clientId.includes(
        WHITE_LABELING_CLIENTID_SEPARATOR
    );
    if (!isWhiteLabeledClient) {
        return { clientId, providerId: null };
    }
    const [whiteLabeledClientId, providerId] = clientId.split(
        WHITE_LABELING_CLIENTID_SEPARATOR
    );
    return { clientId: whiteLabeledClientId, providerId };
};

export const hasPageChanged = (
    query: any,
    nextQuery: any,
    match: Match
): boolean => {
    // Not dependent on match, can be triggered from any v1 or v2 screen
    if (query.selectedTaskId !== nextQuery.selectedTaskId) {
        return true;
    }

    switch (match.path) {
        case v1ScreensUrlEnums.LiveView:
        case v2ScreensUrlEnums.SingleTour:
        case v2ScreensUrlEnums.Tours: {
            if (query.view !== nextQuery.view) {
                return true;
            }
            return false;
        }
        default:
            return false;
    }
};

type PathParams = {
    [key: string]: any
};

export const replacePathParams = (
    path: string,
    pathParams?: PathParams
): string => {
    let pathToGo = path;

    if (pathParams) {
        Object.keys(pathParams).forEach(param => {
            pathToGo = pathToGo.replace(`:${param}`, pathParams[param]);
        });
    }

    return pathToGo;
};
